import classNames from 'classnames';
import React, { createElement } from 'react';
import { withInjectedProps } from '../InjectedPropsContext';
import './Button.scss';

export type IButtonProps = Pick<
  React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  'autoFocus' | 'className' | 'disabled' | 'name' | 'onClick' | 'onKeyDown'
> &
  Pick<React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>, 'href'> & {
    error?: boolean;
    focusRef?: React.Ref<HTMLButtonElement> | React.Ref<HTMLAnchorElement>;
    light?: boolean;
    loading?: boolean;
    noPadding?: boolean;
    primary?: boolean;
    submit?: boolean;
    subtle?: boolean;
    success?: boolean;
    warning?: boolean;
    visible?: boolean;
  };

const ButtonBase: React.FC<React.PropsWithChildren<IButtonProps>> = ({
  autoFocus,
  children,
  className,
  disabled,
  error,
  focusRef,
  href,
  light,
  loading,
  name,
  noPadding,
  onClick,
  onKeyDown,
  primary,
  submit = false,
  subtle,
  success,
  warning,
  visible = true,
}) => {
  if (!visible) return null;
  const tag = href && !disabled ? 'a' : 'button';

  const handleClick = (e: any) => {
    if (!disabled && onClick) {
      e.data = { name };
      onClick(e);
    }
  };

  // Wrap text in a span so we can add conditional margins to icons via CSS.
  if (React.Children.count(children) > 1) {
    children = React.Children.map(children, (child, i) => {
      if (typeof child === 'string') {
        return <span className="Button-Text">{child}</span>;
      } else {
        return child;
      }
    });
  }

  const attributes: any = {
    autoFocus,
    className: classNames(className, 'Button', {
      'Button-error': error,
      'Button-loading': loading,
      'Button-primary': primary,
      'Button-success': success,
      'Button-warning': warning,
      'Button-subtle': subtle,
      'Button-noPadding': noPadding,
      'Button-light': light,
    }),
    disabled,
    ref: focusRef,
    href: disabled ? null : href,
    onClick: handleClick,
    onKeyDown,
  };

  if (submit !== false) {
    attributes.type = 'submit';
  }

  return createElement(tag, attributes, children);
};

const Button = withInjectedProps(ButtonBase);

export { ButtonGroup } from './ButtonGroup';
export { Button, ButtonBase };
export default Button;
